<template>
	<div class="md-layout">
		<div v-if="isLoading" class="loader">
			<div class="lds-circle">
				<div style="position: relative">
					<img src="@/assets/img/logo.png" />
					<div class="loading"></div>
				</div>
			</div>
		</div>
		<div class="md-layout-item">
			<md-card>
				<md-button
					style="position: absolute; top: 3px; left: 10px; font-size: 30px !important"
					class="md-simple md-just-icon md-round modal-default-button"
					@click="openJobsHelp"
				>
					<md-icon>help</md-icon>
				</md-button>
				<h1 class="center-title">Job Invitations</h1>
				<div class="tabs-container">
					<div class="tab" :class="{ active: activeTab === 'pending' }" @click="activeTab = 'pending'">
						Pending Invitations <span v-if="pendingCount">({{ pendingCount }})</span>
					</div>
					<div class="tab" :class="{ active: activeTab === 'previous' }" @click="activeTab = 'previous'">
						Previous Responses <span v-if="previousCount">({{ previousCount }})</span>
					</div>
				</div>
				<md-card-content>
					<JobInvitationTables
						:active-tab="activeTab"
						:queried-data="queriedData"
						:pagination="pagination"
						:current-sort.sync="currentSort"
						:current-sort-order.sync="currentSortOrder"
						:custom-sort="customSort"
						:search-query.sync="searchQuery"
						@accept="acceptInvite"
						@reject="openRejectionModal"
						@open-accept-modal="openAcceptModal"
					/>

					<div v-if="activeTab === 'previous' && queriedData.length === 0" class="text-center p-4 text-gray-600">
						<p>No previous responses found.</p>
					</div>
				</md-card-content>

				<md-card-actions md-alignment="space-between">
					<div>
						<p class="card-category">Showing {{ from + 1 }} to {{ to }} of {{ total }} invitations</p>
					</div>
					<pagination class="pagination-no-border pagination-success" v-model="pagination.currentPage" :per-page="pagination.perPage" :total="total" />
				</md-card-actions>
			</md-card>
		</div>

		<!-- Rejection Modal -->
		<Modal v-if="showModal" @close="showModal = false" size="default">
			<template slot="header">
				<h3 class="rejection-header">Reason for Rejection</h3>
			</template>

			<template slot="body">
				<div class="rejection-modal-body">
					<p class="modal-subtext">
						You are about to reject the invitation for
						<strong>{{ selectedInvite ? selectedInvite.job : '' }}</strong>
						at
						<strong>{{ selectedInvite ? selectedInvite.company : '' }}</strong
						>. Please select a reason for your rejection.
					</p>

					<div class="radio-group">
						<label class="radio-option">
							<input type="radio" v-model="rejectionReason" value="Another role" />
							Another role
						</label>
						<label class="radio-option">
							<input type="radio" v-model="rejectionReason" value="Not interested" />
							Not interested
						</label>
						<label class="radio-option">
							<input type="radio" v-model="rejectionReason" value="Other" />
							Other
						</label>

						<md-field v-if="rejectionReason === 'Other'" class="mt-2">
							<label>Other reason</label>
							<md-input v-model="otherReason" />
						</md-field>
					</div>
				</div>
			</template>

			<template slot="footer">
				<div class="modal-footer-actions">
					<md-button class="md-danger" @click="showModal = false">Cancel</md-button>
					<md-button class="md-success" :disabled="!rejectionReason || (rejectionReason === 'Other' && !otherReason)" @click="confirmRejection">
						Confirm Rejection
					</md-button>
				</div>
			</template>
		</Modal>

		<!-- Accept Invitation Modal -->
		<Modal v-if="showAcceptModal" @close="showAcceptModal = false" size="default">
			<template slot="header">
				<h3 class="rejection-header" style="display: flex; align-items: center; gap: 8px">
					<md-icon style="color: green">check_circle</md-icon>
					Confirm Job Acceptance
				</h3>
			</template>

			<template slot="body">
				<p>
					Are you sure you want to accept the invitation for <strong>{{ selectedInvite ? selectedInvite.job : '' }}</strong> at
					<strong>{{ selectedInvite ? selectedInvite.company : '' }}</strong
					>?
				</p>

				<p>
					By accepting this invitation, you will be taken to the application form to complete your application for this position. You can review all job details before
					submitting your final application.
				</p>
			</template>

			<template slot="footer">
				<div class="modal-footer-actions">
					<md-button class="md-danger" @click="showAcceptModal = false">Cancel</md-button>
					<md-button class="md-success" @click="confirmAcceptInvite">Yes, Accept Invitation</md-button>
				</div>
			</template>
		</Modal>
		<!-- Confirmation Modal -->
		<Modal v-if="showConfirmationModal" @close="showConfirmationModal = false" size="default">
			<template slot="header">
				<h3 class="rejection-header" style="display: flex; align-items: center; gap: 8px">
					<md-icon style="color: green">check_circle</md-icon>
					Confirmation
				</h3>
			</template>

			<template slot="body">
				<p>{{ confirmationMessage }}</p>
			</template>

			<template slot="footer">
				<div class="modal-footer-actions">
					<md-button class="md-success" @click="closeConfirmationModal">OK</md-button>
				</div>
			</template>
		</Modal>
	</div>
</template>

<script>
import { Pagination } from '@/components';
import { helperArticles } from '@/constants/helper-articles.const';
import { getAuth } from 'firebase/auth';
import { updateDoc, getDocs, query, where, collection, Timestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '@/firebase/init';
import Modal from '@/components/Modal.vue';
import JobInvitationTables from '@/components/Tables/JobInvitationTables.vue';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase/init';
export default {
	components: {
		Pagination,
		Modal,
		JobInvitationTables,
	},
	data() {
		return {
			showConfirmationModal: false,
			confirmationMessage: '',

			showAcceptModal: false,
			isLoading: true,
			activeTab: 'pending',
			showModal: false,
			selectedInvite: null,
			rejectionReason: '',
			otherReason: '',
			currentSort: 'company',
			currentSortOrder: 'asc',
			searchQuery: '',
			pagination: {
				perPage: 10,
				currentPage: 1,
				perPageOptions: [5, 10, 20, 50],
			},
			invitations: [],
		};
	},
	async mounted() {
		await this.fetchJobInvitations();
	},
	computed: {
		pendingCount() {
			return this.invitations.filter(i => !i.responseStatus).length;
		},
		previousCount() {
			return this.invitations.filter(i => i.responseStatus).length;
		},
		filteredData() {
			let result = [...this.invitations];
			if (this.searchQuery) {
				const query = this.searchQuery.toLowerCase();
				result = result.filter(i => i.company.toLowerCase().includes(query) || i.job.toLowerCase().includes(query));
			}
			return this.activeTab === 'pending' ? result.filter(i => !i.responseStatus) : result.filter(i => i.responseStatus);
		},
		queriedData() {
			return this.filteredData.slice(this.from, this.to);
		},
		from() {
			return this.pagination.perPage * (this.pagination.currentPage - 1);
		},
		to() {
			return Math.min(this.from + this.pagination.perPage, this.total);
		},
		total() {
			return this.filteredData.length;
		},
	},
	methods: {
		async sendSlackInviteResponse(status, reason = '') {
			const auth = getAuth();
			const user = auth.currentUser;
			if (!user || !this.selectedInvite) return;

			try {
				const notifySlack = httpsCallable(functions, 'notifyJoboxOfInviteResponse');
				await notifySlack({
					studentName: user.displayName || 'Unknown',
					studentEmail: user.email,
					talentPoolName: this.selectedInvite.talentPoolName || 'Unknown',
					companyName: this.selectedInvite.company || 'Unknown',
					jobTitle: this.selectedInvite.job || 'Unknown',
					responseStatus: status,
					rejectionReason: reason || null,
				});
			} catch (error) {
				console.error('[Slack Error]:', error);
			}
		},
		closeConfirmationModal() {
			this.showConfirmationModal = false;

			if (this.selectedInvite?.responseStatus === 'Accepted') {
				this.$router.push(`/student/jobs/micro/application/${this.selectedInvite.id}`);
			}

			this.confirmationMessage = '';
			this.selectedInvite = null;
		},
		openAcceptModal(invite) {
			this.selectedInvite = invite;
			this.showAcceptModal = true;
		},

		async confirmAcceptInvite() {
			try {
				await this.acceptInvite(this.selectedInvite);
				this.showAcceptModal = false;
				this.confirmationMessage = 'You have accepted this invitation.';
				this.showConfirmationModal = true;
			} catch (err) {
				console.error('Navigation error:', err);
			}
		},

		async acceptInvite(invite) {
			try {
				this.isLoading = true;
				const studentDocRef = await this.findStudentDocRef(invite.id);
				const inviteRef = doc(studentDocRef, 'invitations', invite.id);
				await updateDoc(inviteRef, { responseStatus: 'Accepted', status: 'Accepted' });
				await this.sendSlackInviteResponse('Accepted');
				invite.responseStatus = 'Accepted';
				this.$forceUpdate();
			} catch (err) {
				console.error('Error accepting invitation:', err);
				alert('Could not accept the invitation. Please try again.');
			} finally {
				this.isLoading = false;
			}
		},
		openRejectionModal(invite) {
			this.selectedInvite = invite;
			this.rejectionReason = '';
			this.otherReason = '';
			this.showModal = true;
		},
		async confirmRejection() {
			this.isLoading = true;
			const reason = this.rejectionReason === 'Other' ? this.otherReason : this.rejectionReason;
			if (!reason) return alert('Please provide a reason.');
			try {
				const studentDocRef = await this.findStudentDocRef(this.selectedInvite.id);
				const inviteRef = doc(studentDocRef, 'invitations', this.selectedInvite.id);
				await updateDoc(inviteRef, {
					responseStatus: 'Rejected',
					status: 'Rejected',
					rejectionReason: reason,
				});
				await this.sendSlackInviteResponse('Rejected', reason);

				const sendRejectionEmail = httpsCallable(functions, 'sendStudentRejectionInviteEmail');
				await sendRejectionEmail({
					employerEmail: this.selectedInvite.employerEmail,
					employerName: this.selectedInvite.company,
					studentName: getAuth().currentUser.displayName || 'Student',
					jobTitle: this.selectedInvite.job,
					invitationDate: this.selectedInvite.date,
					selectedDeclineReasons: [reason],
					talentPoolName: this.selectedInvite.talentPoolName || 'Talent Pool',
				});

				this.confirmationMessage = `You have rejected this invitation: ${reason}.`;
				this.showConfirmationModal = true;

				this.selectedInvite.responseStatus = 'Rejected';
				this.selectedInvite.rejectionReason = reason;
				this.showModal = false;
				this.$forceUpdate();
			} catch (err) {
				console.error('Error rejecting invitation:', err);
				alert('Could not reject the invitation. Please try again.');
			} finally {
				this.isLoading = false;
			}
		},
		async findStudentDocRef(inviteId) {
			const auth = getAuth();
			const user = auth.currentUser;
			if (!user) throw new Error('User not logged in');
			const userId = user.uid;
			const talentPoolsSnap = await getDocs(collection(db, 'talentPools'));
			for (const poolDoc of talentPoolsSnap.docs) {
				const poolId = poolDoc.id;
				const studentDocRef = doc(db, 'talentPools', poolId, 'talentPoolStudents', userId);
				const studentDocSnap = await getDoc(studentDocRef);
				if (studentDocSnap.exists()) {
					const inviteRef = doc(studentDocRef, 'invitations', inviteId);
					const inviteSnap = await getDoc(inviteRef);
					if (inviteSnap.exists()) return collection(studentDocRef, 'invitations').parent;
				}
			}
			throw new Error('Student doc not found for invitation.');
		},
		async fetchJobInvitations() {
			this.isLoading = true;
			try {
				const user = getAuth().currentUser;
				if (!user) {
					this.invitations = [];
					return;
				}
				const userId = user.uid;
				const poolsSnap = await getDocs(collection(db, 'talentPools'));
				const poolNames = poolsSnap.docs.reduce((map, d) => {
					map[d.id] = d.data().name || 'Unknown Talent Pool';
					return map;
				}, {});
				const perPoolPromises = poolsSnap.docs.map(async poolDoc => {
					const poolId = poolDoc.id;
					const studentsCol = collection(db, 'talentPools', poolId, 'talentPoolStudents');
					let studentSnap = await getDocs(query(studentsCol, where('__name__', '==', userId)));
					let studentRef = studentSnap.docs.length ? studentSnap.docs[0].ref : null;
					if (!studentRef) {
						const allStudents = await getDocs(studentsCol);
						for (const s of allStudents.docs) {
							if (s.data().userId === userId) {
								studentRef = s.ref;
								break;
							}
						}
					}
					if (!studentRef) return [];
					const invitesSnap = await getDocs(collection(studentRef, 'invitations'));
					return invitesSnap.docs.map(inv => {
						const d = inv.data();
						return {
							id: inv.id,
							company: d.companyName || 'Unknown Company',
							job: d.jobName || 'Unknown Role',
							date: d.created instanceof Timestamp ? d.created.toDate().toLocaleDateString() : new Date().toLocaleDateString(),
							status: d.status,
							responseStatus: d.responseStatus || null,
							rejectionReason: d.rejectionReason || null,
							talentPoolName: poolNames[poolId],
							clientId: d.clientId || null,
						};
					});
				});

				const nested = await Promise.all(perPoolPromises);
				const invitationsList = nested.flat();

				const clientIds = [...new Set(invitationsList.map(i => i.clientId).filter(id => id))];
				const emailMap = {};
				for (let i = 0; i < clientIds.length; i += 10) {
					const chunk = clientIds.slice(i, i + 10);
					const usersSnap = await getDocs(query(collection(db, 'users'), where('userId', 'in', chunk)));
					usersSnap.docs.forEach(u => {
						const d = u.data();
						emailMap[d.userId] = d.email || 'Unknown';
					});
				}

				this.invitations = invitationsList.map(inv => ({
					...inv,
					employerEmail: inv.clientId ? emailMap[inv.clientId] || 'Unknown' : 'Unknown',
				}));
			} catch (err) {
				console.error('Error fetching invitations:', err);
				this.invitations = [];
			} finally {
				this.isLoading = false;
			}
		},
		openJobsHelp() {
			window.open(helperArticles.OPEN_JOBS_HELP, '_blank');
		},
		customSort(value) {
			return value.sort((a, b) => {
				const field = this.currentSort;
				const aVal = a[field]?.toString().toLowerCase() || '';
				const bVal = b[field]?.toString().toLowerCase() || '';
				return this.currentSortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
			});
		},
	},
};
</script>

<style scoped>
.rejection-modal-body {
	text-align: left;
}

.modal-subtext {
	margin-bottom: 1.25rem;
	font-size: 0.95rem;
	color: #666;
}

.radio-group {
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
}

.radio-option {
	font-size: 0.95rem;
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.modal-footer-actions {
	display: flex;
	justify-content: flex-end;
	gap: 1rem;
}

.md-button[disabled] {
	background-color: #eee !important;
	color: #999 !important;
	pointer-events: none;
}

.rejection-header {
	color: black;
}
.md-icon {
	font-size: 1.4rem !important;
}
.loader {
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	position: absolute;
	top: 0;
	left: 0;
	background-color: rgba(0, 0, 0, 0.2);
	z-index: 9999;
}
.tabs-container {
	display: flex;
	justify-content: center;
	margin-bottom: 1rem;
	border-bottom: 1px solid #ccc;
}

.tab {
	padding: 10px 20px;
	cursor: pointer;
	font-weight: 500;
	color: #555;
	border-bottom: 2px solid transparent;
	transition: all 0.3s ease;
}

.tab:hover {
	color: #000;
}

.tab.active {
	color: #000;
	border-bottom: 2px solid orange;
}

.center-title {
	font-size: 1.7rem;
	display: flex;
	align-items: center;
	justify-content: center;
}
.badge {
	padding: 0.25rem 0.5rem;
	border-radius: 4px;
	color: white;
	font-size: 0.75rem;
}
.badge.pending {
	background-color: #6b7280;
}
.confirmation {
	font-size: 0.85rem;
	color: #374151;
	margin-top: 0.5rem;
}

.badge.accepted {
	background-color: #64a151;
}
.badge.rejected {
	background-color: #e74c3c;
}
.md-icon-inline {
	vertical-align: middle;
	margin-right: 4px;
}

@media (max-width: 767px) {
	.center-title {
		font-size: 1.5rem;
	}
}
</style>
